import { createEntityAdapter } from '@ngrx/entity';
import { Currency, LoadingStatus } from '@qvs/core';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import * as fromActions from './currencies.actions';
import { onEntityLoadingStatus } from '~/shared/helpers/loading-status-helper';

const currenciesEntityAdapter = createEntityAdapter<Currency>({ selectId: (entity) => entity.currency });

export const initialState = currenciesEntityAdapter.getInitialState({
  loadingStatus: LoadingStatus.IDLE
});

export type State = typeof initialState;

export const currenciesFeatureKey = 'currencies';

const reducer = createReducer(
  initialState,
  ...onEntityLoadingStatus<State>(
    fromActions.loadCurrenciesOnlyIfNotLoadedYet,
    fromActions.loadCurrenciesSuccess,
    fromActions.loadCurrenciesFailure
  ),
  on(fromActions.upsertCurrencies, (state, { entities }) => currenciesEntityAdapter.upsertMany(entities, state))
);

export const currenciesFeature = createFeature({
  name: currenciesFeatureKey,
  reducer
});

const { selectCurrenciesState } = currenciesFeature;

export const selectCurrenciesUnfiltered = createSelector(selectCurrenciesState, currenciesEntityAdapter.getSelectors().selectAll);

export const selectCurrencies = createSelector(selectCurrenciesUnfiltered, (currencies) =>
  // The threshold of 800 is based on the guidelines mentioned here:
  // https://quilvest.atlassian.net/wiki/spaces/MOB/pages/509510119/Currencies+table+and+settings
  currencies.filter((currency) => currency.ranking < 800)
);

export const selectCurrenciesIsLoading = createSelector(
  selectCurrenciesState,
  (state) => state.loadingStatus === LoadingStatus.LOADING
);

export const selectCurrenciesForPayment = (isSwissQrPayment: boolean) =>
  createSelector(selectCurrencies, (currencies) => {
    if (isSwissQrPayment) {
      return currencies.filter((currency) => ['CHF', 'EUR'].includes(currency.currency));
    }
    return currencies;
  });
